// src/components/forms/pending-orders/PendingOrdersDestinationAddress.tsx
// Componente para la sección de Dirección de Destino

import React from 'react'
import type { PendingOrderFormData } from '../../../types/pendingOrdersFormTypes'
import { Input, Select } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { TruckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

interface Departamento {
  Codigo: number | string
  Nombre: string
}

interface Ciudad {
  codigo: string
  ciudad: string
}

interface PendingOrdersDestinationAddressProps {
  formData: PendingOrderFormData
  isExpanded: boolean
  isLiquidateMode: boolean
  hideDestinationFields: string[]
  departamentos: Departamento[]
  ciudades: Ciudad[]
  onToggleExpanded: () => void
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  renderDepartamentoOptions: () => JSX.Element[]
  renderCiudadOptions: () => JSX.Element[]
}

export const PendingOrdersDestinationAddress: React.FC<PendingOrdersDestinationAddressProps> = ({
  formData,
  isExpanded,
  isLiquidateMode,
  hideDestinationFields,
  onToggleExpanded,
  onInputChange,
  renderDepartamentoOptions,
  renderCiudadOptions
}) => {
  // Helper para convertir JSX.Element[] a SelectOption[]
  const getSelectOptions = (renderFunction: () => React.ReactNode): Array<{ value: string | number; label: string; disabled?: boolean }> => {
    try {
      const rendered = renderFunction()
      if (Array.isArray(rendered)) {
        const options: Array<{ value: string | number; label: string; disabled?: boolean }> = []
        
        rendered.forEach((option: any) => {
          if (option && typeof option === 'object') {
            if (option.props) {
              const value = option.props.value || ''
              const children = option.props.children
              const label = typeof children === 'string' 
                ? children 
                : (Array.isArray(children) ? children.join('') : value || '')
              
              if (value !== undefined) {
                options.push({
                  value,
                  label: label || value,
                  disabled: option.props.disabled || false
                })
              }
            }
          }
        })
        
        return options
      }
      return []
    } catch (error) {
      console.error('Error converting options:', error)
      return []
    }
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div 
        className="flex justify-between items-center cursor-pointer"
        onClick={onToggleExpanded}
      >
        <SectionTitle icon={TruckIcon} noMargin>Dirección de Destino</SectionTitle>
        <ChevronDownIcon className={`w-6 h-6 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </div>
      <div
        className={`grid gap-4 transition-all duration-200 ${
          isLiquidateMode
            ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        } ${isExpanded ? 'mt-4' : 'hidden'}`}
      >
        {!hideDestinationFields.includes('nombre') && (
          <FormField
            label="Nombre completo"
            required
          >
            <Input
              type="text"
              name="destinoNombre"
              value={formData.destinoNombre}
              onChange={onInputChange}
              mode="edit"
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('email') && (
          <FormField
            label="Email"
            required
          >
            <Input
              type="email"
              name="destinoEmail"
              value={formData.destinoEmail}
              onChange={onInputChange}
              mode="edit"
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('celular') && (
          <FormField
            label="Celular"
            required
          >
            <Input
              type="tel"
              name="destinoCelular"
              value={formData.destinoCelular}
              onChange={onInputChange}
              mode="edit"
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('pais') && (
          <FormField
            label="País"
            required
          >
            <Input
              type="text"
              name="destinoPais"
              value={formData.destinoPais}
              onChange={onInputChange}
              readOnly
              mode="view"
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('departamento') && (
          <FormField
            label="Departamento"
            required
          >
            <Select
              name="destinoDepartamento"
              value={formData.destinoDepartamento}
              onChange={onInputChange}
              disabled={!isLiquidateMode}
              options={getSelectOptions(renderDepartamentoOptions)}
              placeholder="Seleccione un departamento"
              mode={isLiquidateMode ? 'edit' : 'view'}
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('ciudad') && (
          <FormField
            label="Ciudad"
            required
          >
            <Select
              name="destinoCiudad"
              value={formData.destinoCiudad}
              onChange={onInputChange}
              disabled={!isLiquidateMode}
              options={getSelectOptions(renderCiudadOptions)}
              placeholder="Seleccione una ciudad"
              mode={isLiquidateMode ? 'edit' : 'view'}
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('direccion') && (
          <FormField
            label="Dirección"
            required
          >
            <Input
              type="text"
              name="destinoDireccion"
              value={formData.destinoDireccion}
              onChange={onInputChange}
              readOnly={!isLiquidateMode}
              mode={isLiquidateMode ? 'edit' : 'view'}
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('referencia') && (
          <FormField label="Referencia de dirección">
            <Input
              type="text"
              name="destinoReferencia"
              value={formData.destinoReferencia}
              onChange={onInputChange}
              mode="edit"
            />
          </FormField>
        )}
        {!hideDestinationFields.includes('nit') && (
          <FormField label="Nit / CC">
            <Input
              type="text"
              name="destinoNit"
              value={formData.destinoNit}
              onChange={onInputChange}
              mode="edit"
            />
          </FormField>
        )}
      </div>
    </div>
  )
}

